import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const TITLES = { login: "Welcome back", register: "Join the brotherhood", forgot: "Reset your password" };
const SUBTITLES = {
  login: "Sign in to post and connect with other fathers.",
  register: "Create a free account to join the community forum.",
  forgot: "Enter your email and we'll send a secure link to choose a new password.",
};

function ForgotPasswordForm({ email, setEmail, onBack }) {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setLoading(false); }
  };
  if (sent) return (
    <div className="text-center py-4" data-testid="forgot-sent">
      <MailCheck className="h-10 w-10 text-emerald-400 mx-auto" />
      <p className="text-slate-200 mt-3 text-sm">If an account exists for <strong className="text-white">{email}</strong>, a reset link is on its way. It works once and expires in 1 hour.</p>
      <p className="text-xs text-slate-500 mt-2">Check your spam folder if it doesn't arrive in a minute.</p>
      <Button type="button" onClick={onBack} variant="outline" className="mt-5 border-[#334862] bg-transparent text-slate-100 hover:bg-[#0B111E] rounded-full" data-testid="forgot-back">Back to sign in</Button>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-4 mt-2" data-testid="forgot-form">
      <div className="space-y-1.5">
        <Label htmlFor="forgot-email" className="text-slate-300">Email</Label>
        <Input id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
          placeholder="you@example.com" className="bg-[#0B111E] border-[#223552] text-white" data-testid="forgot-email-input" />
      </div>
      {error && <p className="text-sm text-crimson bg-crimson/10 border border-crimson/30 rounded-md px-3 py-2" data-testid="forgot-error">{error}</p>}
      <Button type="submit" disabled={loading} className="w-full bg-[#E5A93C] hover:bg-[#F5BA4E] text-[#0B111E] font-semibold rounded-full" data-testid="forgot-submit">
        {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send reset link
      </Button>
      <button type="button" onClick={onBack} className="w-full text-sm text-slate-400 hover:text-amber-400 inline-flex items-center justify-center gap-1" data-testid="forgot-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
      </button>
    </form>
  );
}

export const AuthDialog = ({ open, onOpenChange }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setError(""); setPassword(""); };

  const signInWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = mode === "login"
      ? await login(email, password)
      : await register(name, email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(mode === "login" ? "Welcome back, brother." : "Welcome to the brotherhood.");
      onOpenChange(false);
      setName(""); setEmail(""); setPassword("");
    } else {
      setError(res.error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111B2C] border-[#223552] text-slate-200 sm:max-w-[440px]" data-testid="auth-dialog">
        <DialogHeader>
          <div className="mx-auto mb-2 h-12 w-12 rounded-xl bg-gradient-to-br from-[#E5A93C] to-[#C86A36] flex items-center justify-center">
            <Shield className="h-6 w-6 text-[#0B111E]" strokeWidth={2.5} />
          </div>
          <DialogTitle className="text-center font-heading text-2xl text-white">
            {TITLES[mode]}
          </DialogTitle>
          <DialogDescription className="text-center text-sm text-slate-400">
            {SUBTITLES[mode]}
          </DialogDescription>
        </DialogHeader>

        {mode === "forgot" ? (
          <ForgotPasswordForm email={email} setEmail={setEmail} onBack={() => { setMode("login"); reset(); }} />
        ) : (
        <>
        <button type="button" onClick={signInWithGoogle} data-testid="auth-google-button"
          className="w-full mt-2 flex items-center justify-center gap-3 rounded-full border border-[#223552] bg-[#0B111E] hover:bg-[#172338] text-white font-medium h-11 transition-colors">
          <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.5-1.66 4.4-5.5 4.4-3.31 0-6-2.74-6-6.1s2.69-6.1 6-6.1c1.88 0 3.14.8 3.86 1.49l2.63-2.53C16.9 3.08 14.66 2 12 2 6.98 2 2.9 6.06 2.9 11.1S6.98 20.2 12 20.2c6.06 0 8.7-4.25 8.7-6.83 0-.46-.05-.81-.11-1.17H12z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-4">
          <span className="h-px flex-1 bg-[#223552]" />
          <span className="text-xs text-slate-500">or use email</span>
          <span className="h-px flex-1 bg-[#223552]" />
        </div>

        <form onSubmit={submit} className="space-y-4 mt-2">
          {mode === "register" && (
            <div className="space-y-1.5">
              <Label htmlFor="auth-name" className="text-slate-300">Display name</Label>
              <Input id="auth-name" value={name} onChange={(e) => setName(e.target.value)} required
                placeholder="How other dads will see you" className="bg-[#0B111E] border-[#223552] text-white" data-testid="auth-name-input" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="auth-email" className="text-slate-300">Email</Label>
            <Input id="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              placeholder="you@example.com" className="bg-[#0B111E] border-[#223552] text-white" data-testid="auth-email-input" />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="auth-password" className="text-slate-300">Password</Label>
              {mode === "login" && (
                <button type="button" onClick={() => { setMode("forgot"); reset(); }} className="text-xs text-amber-400 hover:underline" data-testid="auth-forgot-link">Forgot password?</button>
              )}
            </div>
            <Input id="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
              placeholder="At least 6 characters" className="bg-[#0B111E] border-[#223552] text-white" data-testid="auth-password-input" />
          </div>

          {error && <p className="text-sm text-crimson bg-crimson/10 border border-crimson/30 rounded-md px-3 py-2" data-testid="auth-error">{error}</p>}

          <Button type="submit" disabled={loading}
            className="w-full bg-[#E5A93C] hover:bg-[#F5BA4E] text-[#0B111E] font-semibold rounded-full" data-testid="auth-submit-button">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {mode === "login" ? "Sign in" : "Create account"}
          </Button>
        </form>

        <p className="text-center text-sm text-slate-400 mt-1">
          {mode === "login" ? "New here?" : "Already have an account?"}{" "}
          <button
            onClick={() => { setMode(mode === "login" ? "register" : "login"); reset(); }}
            className="text-amber-400 font-semibold hover:underline" data-testid="auth-toggle-mode"
          >
            {mode === "login" ? "Create a free account" : "Sign in"}
          </button>
        </p>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};
